import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success(t("auth.createdToast"));
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main data-testid="register-page" className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-3xl border border-zinc-100 p-8 md:p-10">
        <h1 className="font-display font-extrabold text-3xl tracking-tighter text-zinc-900">{t("auth.createTitle")}</h1>
        <p className="mt-2 text-sm text-zinc-500">{t("auth.createSub")}</p>

        <Button data-testid="google-register-btn" type="button" onClick={googleLogin} variant="outline" className="mt-8 w-full rounded-full h-12 border-2 border-zinc-200 hover:border-zinc-400 bg-white text-zinc-900 font-semibold">
          {t("auth.continueGoogle")}
        </Button>
        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-400">
          <div className="flex-1 h-px bg-zinc-200" /> {t("auth.orEmail")} <div className="flex-1 h-px bg-zinc-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("auth.name")}</Label>
            <Input data-testid="register-name" name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("auth.email")}</Label>
            <Input data-testid="register-email" type="email" name="email" value={form.email} onChange={onChange} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-zinc-800">{t("auth.password")}</Label>
            <Input data-testid="register-password" type="password" name="password" value={form.password} onChange={onChange} minLength={6} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
          </div>
          <Button data-testid="register-submit-btn" type="submit" disabled={submitting} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12 text-base font-semibold">
            {submitting ? t("auth.creating") : t("auth.createBtn")}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-zinc-500">
          {t("auth.haveAccount")}{" "}
          <Link data-testid="to-login-link" to="/login" className="font-semibold text-brand hover:underline">{t("auth.signIn")}</Link>
        </div>
      </div>
    </main>
  );
}
